import { useEffect, useRef } from 'react';
import { useAppStore } from '@/store/appStore';

/**
* Hook per gestire le scorciatoie da tastiera globali dell'applicazione.
* Gestisce:
* - Command Palette (Cmd/Ctrl + K)
* - Settings (Cmd/Ctrl + ,)
* - New Task (Cmd/Ctrl + N)
* - Quick Search (Cmd/Ctrl + /)
* - View switching (Cmd/Ctrl + 1-8)
* - Clear filters (Cmd/Ctrl + A)
* - Cache verification (Shift + Cmd/Ctrl + 0)
* - Close modals/panels (Escape)
*
* Non interferisce con gli input di testo (input/textarea).
*
* Uses refs for reactive state to register the keydown listener only once,
* avoiding repeated add/remove on every state change.
*/
export function useKeyboardShortcuts() {
  const selectedTaskId = useAppStore((state) => state.selectedTaskId);
  const isUserSettingsOpen = useAppStore((state) => state.isUserSettingsOpen);
  const isProjectSettingsOpen = useAppStore((state) => state.isProjectSettingsOpen);
  const isCreateTaskOpen = useAppStore((state) => state.isCreateTaskOpen);
  const showCacheVerification = useAppStore((state) => state.showCacheVerification);

  const openCommandPalette = useAppStore((state) => state.openCommandPalette);
  const openUserSettings = useAppStore((state) => state.openUserSettings);
  const openCreateTask = useAppStore((state) => state.openCreateTask);
  const triggerSearchFocus = useAppStore((state) => state.triggerSearchFocus);
  const setViewMode = useAppStore((state) => state.setViewMode);
  const setFilters = useAppStore((state) => state.setFilters);
  const openCacheVerification = useAppStore((state) => state.openCacheVerification);
  const closeCacheVerification = useAppStore((state) => state.closeCacheVerification);
  const closeCreateTask = useAppStore((state) => state.closeCreateTask);
  const closeUserSettings = useAppStore((state) => state.closeUserSettings);
  const closeProjectSettings = useAppStore((state) => state.closeProjectSettings);
  const setSelectedTaskId = useAppStore((state) => state.setSelectedTaskId);

  // Stabilize reactive values in a ref so the effect can register once
  const stateRef = useRef({
    selectedTaskId,
    isUserSettingsOpen,
    isProjectSettingsOpen,
    isCreateTaskOpen,
    showCacheVerification,
  });
  useEffect(() => {
    stateRef.current = {
      selectedTaskId,
      isUserSettingsOpen,
      isProjectSettingsOpen,
      isCreateTaskOpen,
      showCacheVerification,
    };
  });

  // Stabilize action refs (Zustand selectors return stable references, but be safe)
  const actionsRef = useRef({
    openCommandPalette,
    openUserSettings,
    openCreateTask,
    triggerSearchFocus,
    setViewMode,
    setFilters,
    openCacheVerification,
    closeCacheVerification,
    closeCreateTask,
    closeUserSettings,
    closeProjectSettings,
    setSelectedTaskId,
  });
  useEffect(() => {
    actionsRef.current = {
      openCommandPalette,
      openUserSettings,
      openCreateTask,
      triggerSearchFocus,
      setViewMode,
      setFilters,
      openCacheVerification,
      closeCacheVerification,
      closeCreateTask,
      closeUserSettings,
      closeProjectSettings,
      setSelectedTaskId,
    };
  });

  useEffect(() => {
  const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if typing in input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
    return;
      }

      const actions = actionsRef.current;
      const state = stateRef.current;

      // Command palette: Cmd/Ctrl + K
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
    e.preventDefault();
    actions.openCommandPalette();
    return;
      }

      // Settings: Cmd/Ctrl + ,
      if ((e.metaKey || e.ctrlKey) && e.key === ',') {
    e.preventDefault();
    actions.openUserSettings('appearance');
    return;
      }

      // New task: Cmd/Ctrl + N
      if ((e.metaKey || e.ctrlKey) && e.key === 'n') {
    e.preventDefault();
    actions.openCreateTask();
    return;
      }

      // Quick search: Cmd/Ctrl + /
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
    e.preventDefault();
    actions.triggerSearchFocus();
    return;
      }

      // View shortcuts: Cmd/Ctrl + 1/2/3/4/5/6
      if (e.metaKey || e.ctrlKey) {
    if (e.key === '1') {
          e.preventDefault();
          actions.setViewMode('list');
    } else if (e.key === '2') {
          e.preventDefault();
          actions.setViewMode('board');
    } else if (e.key === '3') {
          e.preventDefault();
          actions.setViewMode('gantt');
    } else if (e.key === '4') {
          e.preventDefault();
          actions.setViewMode('inbox');
    } else if (e.key === '5') {
          e.preventDefault();
          actions.setViewMode('team');
    } else if (e.key === '6') {
          e.preventDefault();
          actions.setViewMode('timesheet');
    } else if (e.key === '7') {
          e.preventDefault();
          actions.setViewMode('sprint');
    } else if (e.key === '8') {
          e.preventDefault();
          actions.setViewMode('calendar');
    } else if (e.key === 'a' && !e.shiftKey) {
          e.preventDefault();
          actions.setFilters({ path: null });
    } else if (e.key === '0' && e.shiftKey) {
          // Shift+Cmd+0 to open cache verification
          e.preventDefault();
          actions.openCacheVerification();
    }
      }

      // Close modals/panels: Escape
      if (e.key === 'Escape') {
    if (state.showCacheVerification) {
          actions.closeCacheVerification();
    } else if (state.isCreateTaskOpen) {
          actions.closeCreateTask();
    } else if (state.isUserSettingsOpen) {
          actions.closeUserSettings();
    } else if (state.isProjectSettingsOpen) {
          actions.closeProjectSettings();
    } else if (state.selectedTaskId) {
          actions.setSelectedTaskId(null);
    }
      }
  };

  window.addEventListener('keydown', handleKeyDown);
  return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
}


